import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Pause,
  CheckCircle,
  Clock,
  Database,
  RefreshCw,
  XCircle,
  Search,
  Activity,
  Eye,
} from 'lucide-react';
import { Breadcrumb } from '@/components/Breadcrumb';
import { Tooltip } from '@/components/Tooltip';
import { StatusBadge } from '@/components/Badge';
import { EmptyState } from '@/components/EmptyState';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useToast } from '@/contexts/ToastContext';

interface PipelineExecution {
  traceId: string;
  exam: string;
  oeCode: string;
  questionType: string;
  count: number;
  difficulty: string;
  status: 'in_progress' | 'completed' | 'failed' | 'validating';
  currentStage: number;
  totalStages: number;
  startTime: string;
  elapsedMs: number;
  estimatedRemainingMs: number;
  stages: {
    name: string;
    status: 'completed' | 'in_progress' | 'pending' | 'failed';
    latencyMs?: number;
    percentage?: number;
  }[];
  mcpServers: {
    name: string;
    status: 'completed' | 'in_progress' | 'pending' | 'failed';
    latencyMs?: number;
  }[];
  metadata?: {
    tokensUsed?: number;
    cost?: number;
    chunksRetrieved?: number;
    questionsGenerated?: number;
  };
}

export function ExecutionMonitor() {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedExecution, setSelectedExecution] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const refreshTimerRef = useRef<number | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });

  const handleRefresh = () => {
    setIsRefreshing(true);
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }
    refreshTimerRef.current = window.setTimeout(() => setIsRefreshing(false), 1000);
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, []);

  const activeExecutions: PipelineExecution[] = [
    {
      traceId: 'trace_abc123',
      exam: 'CPA-10',
      oeCode: 'OE.01.01',
      questionType: 'multiple_choice',
      count: 3,
      difficulty: 'medium',
      status: 'in_progress',
      currentStage: 3,
      totalStages: 4,
      startTime: '2 min ago',
      elapsedMs: 2340,
      estimatedRemainingMs: 1260,
      stages: [
        { name: 'Query Analyzer', status: 'completed', latencyMs: 780, percentage: 15 },
        { name: 'MCP Router', status: 'completed', latencyMs: 1450, percentage: 28 },
        { name: 'Reranker', status: 'in_progress', percentage: 11 },
        { name: 'LLM Generation', status: 'pending', percentage: 38 },
      ],
      mcpServers: [
        { name: 'Pinecone', status: 'completed', latencyMs: 210 },
        { name: 'OE Database', status: 'completed', latencyMs: 95 },
        { name: 'Regulations', status: 'completed', latencyMs: 315 },
        { name: 'Previous Exams', status: 'in_progress' },
        { name: 'Glossary', status: 'pending' },
        { name: 'PDF Content', status: 'pending' },
        { name: 'ANBIMA API', status: 'pending' },
      ],
      metadata: {
        chunksRetrieved: 45,
      },
    },
    {
      traceId: 'trace_def456',
      exam: 'CPA-20',
      oeCode: 'OE.02.03',
      questionType: 'true_false',
      count: 5,
      difficulty: 'easy',
      status: 'in_progress',
      currentStage: 1,
      totalStages: 4,
      startTime: '1 min ago',
      elapsedMs: 650,
      estimatedRemainingMs: 4500,
      stages: [
        { name: 'Query Analyzer', status: 'in_progress', percentage: 15 },
        { name: 'MCP Router', status: 'pending', percentage: 28 },
        { name: 'Reranker', status: 'pending', percentage: 11 },
        { name: 'LLM Generation', status: 'pending', percentage: 38 },
      ],
      mcpServers: [
        { name: 'Pinecone', status: 'pending' },
        { name: 'OE Database', status: 'pending' },
        { name: 'Regulations', status: 'pending' },
        { name: 'Previous Exams', status: 'pending' },
        { name: 'Glossary', status: 'pending' },
        { name: 'PDF Content', status: 'pending' },
        { name: 'ANBIMA API', status: 'pending' },
      ],
    },
    {
      traceId: 'trace_ghi789',
      exam: 'CEA',
      oeCode: 'OE.03.01',
      questionType: 'multiple_choice',
      count: 10,
      difficulty: 'hard',
      status: 'validating',
      currentStage: 4,
      totalStages: 4,
      startTime: '30 sec ago',
      elapsedMs: 4820,
      estimatedRemainingMs: 300,
      stages: [
        { name: 'Query Analyzer', status: 'completed', latencyMs: 820, percentage: 15 },
        { name: 'MCP Router', status: 'completed', latencyMs: 1580, percentage: 28 },
        { name: 'Reranker', status: 'completed', latencyMs: 620, percentage: 11 },
        { name: 'LLM Generation', status: 'completed', latencyMs: 1800, percentage: 38 },
      ],
      mcpServers: [
        { name: 'Pinecone', status: 'completed', latencyMs: 225 },
        { name: 'OE Database', status: 'completed', latencyMs: 102 },
        { name: 'Regulations', status: 'completed', latencyMs: 340 },
        { name: 'Previous Exams', status: 'completed', latencyMs: 145 },
        { name: 'Glossary', status: 'completed', latencyMs: 88 },
        { name: 'PDF Content', status: 'completed', latencyMs: 195 },
        { name: 'ANBIMA API', status: 'failed' },
      ],
      metadata: {
        chunksRetrieved: 78,
        tokensUsed: 6800,
        cost: 0.018,
        questionsGenerated: 10,
      },
    },
  ];

  const recentExecutions: PipelineExecution[] = [
    {
      traceId: 'trace_jkl012',
      exam: 'CPA-10',
      oeCode: 'OE.01.02',
      questionType: 'multiple_choice',
      count: 3,
      difficulty: 'medium',
      status: 'completed',
      currentStage: 4,
      totalStages: 4,
      startTime: '5 min ago',
      elapsedMs: 5120,
      estimatedRemainingMs: 0,
      stages: [
        { name: 'Query Analyzer', status: 'completed', latencyMs: 795, percentage: 15 },
        { name: 'MCP Router', status: 'completed', latencyMs: 1485, percentage: 28 },
        { name: 'Reranker', status: 'completed', latencyMs: 590, percentage: 11 },
        { name: 'LLM Generation', status: 'completed', latencyMs: 2050, percentage: 38 },
      ],
      mcpServers: [
        { name: 'Pinecone', status: 'completed', latencyMs: 205 },
        { name: 'OE Database', status: 'completed', latencyMs: 92 },
        { name: 'Regulations', status: 'completed', latencyMs: 310 },
        { name: 'Previous Exams', status: 'completed', latencyMs: 138 },
        { name: 'Glossary', status: 'completed', latencyMs: 82 },
        { name: 'PDF Content', status: 'completed', latencyMs: 178 },
        { name: 'ANBIMA API', status: 'completed', latencyMs: 425 },
      ],
      metadata: {
        chunksRetrieved: 52,
        tokensUsed: 5900,
        cost: 0.015,
        questionsGenerated: 3,
      },
    },
    {
      traceId: 'trace_mno345',
      exam: 'CPA-20',
      oeCode: 'OE.02.01',
      questionType: 'multiple_choice',
      count: 5,
      difficulty: 'hard',
      status: 'failed',
      currentStage: 2,
      totalStages: 4,
      startTime: '8 min ago',
      elapsedMs: 2450,
      estimatedRemainingMs: 0,
      stages: [
        { name: 'Query Analyzer', status: 'completed', latencyMs: 810, percentage: 15 },
        { name: 'MCP Router', status: 'failed', percentage: 28 },
        { name: 'Reranker', status: 'pending', percentage: 11 },
        { name: 'LLM Generation', status: 'pending', percentage: 38 },
      ],
      mcpServers: [
        { name: 'Pinecone', status: 'failed' },
        { name: 'OE Database', status: 'completed', latencyMs: 98 },
        { name: 'Regulations', status: 'pending' },
        { name: 'Previous Exams', status: 'pending' },
        { name: 'Glossary', status: 'pending' },
        { name: 'PDF Content', status: 'pending' },
        { name: 'ANBIMA API', status: 'pending' },
      ],
      metadata: {
        chunksRetrieved: 12,
      },
    },
  ];

  const allExecutions = [...activeExecutions, ...recentExecutions];
  const filteredExecutions = allExecutions.filter((exec) => {
    const matchesStatus = filterStatus === 'all' || exec.status === filterStatus;
    const matchesSearch =
      searchTerm === '' ||
      exec.traceId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      exec.oeCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
      exec.exam.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <Breadcrumb items={[
        { label: 'RAG System', path: '/rag' },
        { label: 'Task Monitor' }
      ]} />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Pipeline Execution Monitor</h1>
          <p className="text-sm text-gray-600 mt-1">
            Real-time tracking of question generation pipeline executions
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Tooltip content="Manually refresh execution data">
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 flex items-center disabled:bg-gray-100"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </Tooltip>
          <Tooltip content={autoRefresh ? "Auto-refresh is enabled (updates every 5 seconds)" : "Enable auto-refresh to update execution data automatically"}>
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`px-4 py-2 rounded-xl text-sm font-medium flex items-center ${
                autoRefresh
                  ? 'bg-primary-600 text-white hover:bg-primary-700'
                  : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Auto-Refresh: {autoRefresh ? 'ON' : 'OFF'}
            </button>
          </Tooltip>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-6">
        <Tooltip content="Number of pipeline executions currently running">
          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl border border-blue-200 p-6">
            <Activity className="w-8 h-8 text-blue-600 mb-2" />
            <p className="text-sm text-gray-600 mb-1">Active Executions</p>
            <p className="text-3xl font-bold text-gray-900">{activeExecutions.length}</p>
          </div>
        </Tooltip>

        <Tooltip content="Total successful executions completed today">
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-200 p-6">
            <CheckCircle className="w-8 h-8 text-green-600 mb-2" />
            <p className="text-sm text-gray-600 mb-1">Completed Today</p>
            <p className="text-3xl font-bold text-gray-900">127</p>
          </div>
        </Tooltip>

        <Tooltip content="Number of failed executions today (check logs for details)">
          <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-xl border border-red-200 p-6">
            <XCircle className="w-8 h-8 text-red-600 mb-2" />
            <p className="text-sm text-gray-600 mb-1">Failed Today</p>
            <p className="text-3xl font-bold text-gray-900">3</p>
          </div>
        </Tooltip>

        <Tooltip content="Average time to complete a full pipeline execution">
          <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl border border-orange-200 p-6">
            <Clock className="w-8 h-8 text-#ea7500 mb-2" />
            <p className="text-sm text-gray-600 mb-1">Avg Duration</p>
            <p className="text-3xl font-bold text-gray-900">5.2s</p>
          </div>
        </Tooltip>
      </div>

      {/* Filters & Search */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Tooltip content="Show all executions regardless of status">
            <button
              onClick={() => setFilterStatus('all')}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${
                filterStatus === 'all'
                  ? 'bg-primary-600 text-white'
                  : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
              }`}
            >
              All ({allExecutions.length})
            </button>
          </Tooltip>
          <Tooltip content="Show only executions currently running">
            <button
              onClick={() => setFilterStatus('in_progress')}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${
                filterStatus === 'in_progress'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
              }`}
            >
              In Progress ({activeExecutions.filter((e) => e.status === 'in_progress').length})
            </button>
          </Tooltip>
          <Tooltip content="Show only successfully completed executions">
            <button
              onClick={() => setFilterStatus('completed')}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${
                filterStatus === 'completed'
                  ? 'bg-green-600 text-white'
                  : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
              }`}
            >
              Completed
            </button>
          </Tooltip>
          <Tooltip content="Show only executions that encountered errors">
            <button
              onClick={() => setFilterStatus('failed')}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${
                filterStatus === 'failed'
                  ? 'bg-red-600 text-white'
                  : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
              }`}
            >
              Failed
            </button>
          </Tooltip>
        </div>

        <Tooltip content="Search by trace ID, OE code, or exam name">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by trace ID, OE code, or exam..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 pr-4 py-2 w-80 bg-white border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </Tooltip>
      </div>

      {/* Executions List */}
      <div className="space-y-4">
        {filteredExecutions.map((execution) => (
          <div
            key={execution.traceId}
            className={`bg-white rounded-xl border-2 transition-all cursor-pointer ${
              selectedExecution === execution.traceId
                ? 'border-primary-500 shadow-lg'
                : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
            }`}
            onClick={() =>
              setSelectedExecution(
                selectedExecution === execution.traceId ? null : execution.traceId
              )
            }
          >
            {/* Execution Header */}
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center space-x-3 mb-2">
                    <Tooltip content="Unique trace ID for this execution">
                      <span className="text-sm font-mono text-gray-600">
                        {execution.traceId}
                      </span>
                    </Tooltip>
                    <Tooltip content={`Exam: ${execution.exam}`}>
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded">
                        {execution.exam}
                      </span>
                    </Tooltip>
                    <Tooltip content={`Learning Objective: ${execution.oeCode}`}>
                      <span className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded">
                        {execution.oeCode}
                      </span>
                    </Tooltip>
                    <StatusBadge status={execution.status} />
                  </div>
                  <div className="flex items-center space-x-4 text-xs text-gray-600">
                    <Tooltip content={`Generating ${execution.count} ${execution.questionType.replace('_', ' ')} questions`}>
                      <span>{execution.count} {execution.questionType.replace('_', ' ')}</span>
                    </Tooltip>
                    <span>•</span>
                    <Tooltip content={`Question difficulty: ${execution.difficulty}`}>
                      <span className="capitalize">{execution.difficulty}</span>
                    </Tooltip>
                    <span>•</span>
                    <Tooltip content="Time since execution started">
                      <span className="flex items-center">
                        <Clock className="w-3 h-3 mr-1" />
                        {execution.startTime}
                      </span>
                    </Tooltip>
                  </div>
                </div>

                <Tooltip content={execution.estimatedRemainingMs > 0 ? `Estimated ~${(execution.estimatedRemainingMs / 1000).toFixed(1)}s remaining` : "Execution completed"}>
                  <div className="text-right">
                    <p className="text-sm text-gray-600">Elapsed</p>
                    <p className="text-lg font-bold text-gray-900">
                      {(execution.elapsedMs / 1000).toFixed(1)}s
                    </p>
                    {execution.estimatedRemainingMs > 0 && (
                      <p className="text-xs text-gray-500">
                        ~{(execution.estimatedRemainingMs / 1000).toFixed(1)}s remaining
                      </p>
                    )}
                  </div>
                </Tooltip>
              </div>

              {/* Pipeline Progress */}
              <div className="space-y-3">
                <div className="flex items-center justify-between text-xs text-gray-600">
                  <Tooltip content="Current pipeline stage out of total stages">
                    <span>
                      Stage {execution.currentStage}/{execution.totalStages}
                    </span>
                  </Tooltip>
                  <Tooltip content="Overall completion percentage">
                    <span>
                      {Math.round(
                        (execution.currentStage / execution.totalStages) * 100
                      )}
                      % complete
                    </span>
                  </Tooltip>
                </div>

                {/* Progress Bar */}
                <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary-500 transition-all duration-500"
                    style={{
                      width: `${(execution.currentStage / execution.totalStages) * 100}%`,
                    }}
                  />
                </div>

                {/* Stage Details */}
                <div className="grid grid-cols-4 gap-2">
                  {execution.stages.map((stage, idx) => (
                    <Tooltip
                      key={idx}
                      content={`${stage.name}: ${stage.status}${stage.latencyMs ? ` (${stage.latencyMs}ms)` : ''}`}
                    >
                      <div
                        className={`p-3 rounded-lg border ${
                          stage.status === 'completed'
                            ? 'bg-green-50 border-green-200'
                            : stage.status === 'in_progress'
                            ? 'bg-blue-50 border-blue-200 animate-pulse'
                            : stage.status === 'failed'
                            ? 'bg-red-50 border-red-200'
                            : 'bg-gray-50 border-gray-200'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-medium text-gray-900">
                            {idx + 1}. {stage.name}
                          </span>
                          {stage.status === 'completed' && (
                            <CheckCircle className="w-4 h-4 text-green-600" />
                          )}
                          {stage.status === 'in_progress' && (
                            <RefreshCw className="w-4 h-4 text-blue-600 animate-spin" />
                          )}
                          {stage.status === 'failed' && (
                            <XCircle className="w-4 h-4 text-red-600" />
                          )}
                          {stage.status === 'pending' && (
                            <Pause className="w-4 h-4 text-gray-400" />
                          )}
                        </div>
                        {stage.latencyMs !== undefined && (
                          <p className="text-xs text-gray-600">{stage.latencyMs}ms</p>
                        )}
                      </div>
                    </Tooltip>
                  ))}
                </div>
              </div>
            </div>

            {/* Expanded Details */}
            {selectedExecution === execution.traceId && (
              <div className="border-t border-gray-200 p-6 bg-gray-50">
                <h4 className="text-sm font-semibold text-gray-900 mb-4">
                  MCP Server Status (7 Knowledge Sources)
                </h4>
                <div className="grid grid-cols-7 gap-2 mb-6">
                  {execution.mcpServers.map((server) => (
                    <Tooltip
                      key={server.name}
                      content={`${server.name}: ${server.status}${server.latencyMs ? ` (${server.latencyMs}ms)` : ''}`}
                    >
                      <div
                        className={`p-3 rounded-lg border text-center ${
                          server.status === 'completed'
                            ? 'bg-green-50 border-green-200'
                            : server.status === 'in_progress'
                            ? 'bg-blue-50 border-blue-200'
                            : server.status === 'failed'
                            ? 'bg-red-50 border-red-200'
                            : 'bg-gray-50 border-gray-200'
                        }`}
                      >
                        {server.status === 'completed' && (
                          <CheckCircle className="w-4 h-4 text-green-600 mx-auto mb-1" />
                        )}
                        {server.status === 'in_progress' && (
                          <RefreshCw className="w-4 h-4 text-blue-600 mx-auto mb-1 animate-spin" />
                        )}
                        {server.status === 'failed' && (
                          <XCircle className="w-4 h-4 text-red-600 mx-auto mb-1" />
                        )}
                        {server.status === 'pending' && (
                          <Database className="w-4 h-4 text-gray-400 mx-auto mb-1" />
                        )}
                        <p className="text-xs font-medium text-gray-900">{server.name}</p>
                        {server.latencyMs && (
                          <p className="text-xs text-gray-600 mt-0.5">{server.latencyMs}ms</p>
                        )}
                      </div>
                    </Tooltip>
                  ))}
                </div>

                {execution.metadata && (
                  <div className="grid grid-cols-4 gap-4 p-4 bg-white rounded-lg border border-gray-200">
                    {execution.metadata.chunksRetrieved !== undefined && (
                      <Tooltip content="Number of text chunks retrieved from knowledge sources">
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Chunks Retrieved</p>
                          <p className="text-lg font-bold text-gray-900">
                            {execution.metadata.chunksRetrieved}
                          </p>
                        </div>
                      </Tooltip>
                    )}
                    {execution.metadata.tokensUsed !== undefined && (
                      <Tooltip content="Total tokens consumed by LLM (input + output)">
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Tokens Used</p>
                          <p className="text-lg font-bold text-gray-900">
                            {execution.metadata.tokensUsed.toLocaleString()}
                          </p>
                        </div>
                      </Tooltip>
                    )}
                    {execution.metadata.cost !== undefined && (
                      <Tooltip content="Estimated cost for this execution (based on token usage)">
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Cost</p>
                          <p className="text-lg font-bold text-gray-900">
                            ${execution.metadata.cost.toFixed(3)}
                          </p>
                        </div>
                      </Tooltip>
                    )}
                    {execution.metadata.questionsGenerated !== undefined && (
                      <Tooltip content="Number of questions successfully generated">
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Questions Generated</p>
                          <p className="text-lg font-bold text-gray-900">
                            {execution.metadata.questionsGenerated}
                          </p>
                        </div>
                      </Tooltip>
                    )}
                  </div>
                )}

                <div className="flex items-center justify-end space-x-3 mt-4">
                  <Tooltip content="View detailed trace information including all MCP calls and context">
                    <button
                      onClick={() => navigate(`/rag/trace-explorer?traceId=${execution.traceId}`)}
                      className="px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 flex items-center"
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      View Trace Details
                    </button>
                  </Tooltip>
                  {execution.status === 'in_progress' && (
                    <Tooltip content="Stop this execution and cancel remaining pipeline stages">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setConfirmDialog({
                            isOpen: true,
                            title: 'Cancel Execution',
                            message: 'Are you sure you want to cancel this execution? This action cannot be undone.',
                            onConfirm: () => {
                              showToast('info', 'Execution Cancelled', `Execution ${execution.traceId} has been cancelled.`);
                              setConfirmDialog({ ...confirmDialog, isOpen: false });
                            },
                          });
                        }}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700"
                      >
                        Cancel Execution
                      </button>
                    </Tooltip>
                  )}
                  {execution.status === 'failed' && (
                    <Tooltip content="Retry this execution with the same parameters">
                      <button className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700">
                        Retry Execution
                      </button>
                    </Tooltip>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {filteredExecutions.length === 0 && (
        <EmptyState
          icon={Activity}
          title="No Executions Found"
          description={
            searchTerm
              ? `No executions match "${searchTerm}". Try a different search term.`
              : filterStatus !== 'all'
              ? `No ${filterStatus} executions found. Try selecting a different filter.`
              : 'No pipeline executions have been started yet. Generate a question to see executions here.'
          }
          action={
            searchTerm || filterStatus !== 'all'
              ? {
                  label: 'Clear Filters',
                  onClick: () => {
                    setSearchTerm('');
                    setFilterStatus('all');
                  },
                }
              : {
                  label: 'Generate Questions',
                  to: '/generate',
                }
          }
        />
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        confirmText="Cancel Execution"
      />
    </div>
  );
}
